import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import type {
  AddOnDto,
  AdminCustomerDetailDto,
  AdminCustomerSummaryDto,
  AdminLeadDto,
  AdminOverviewDto,
  AdminPagedResult,
  AdminProvisioningDto,
  AdminWebhookEventDto,
  PlanDto,
  PurchaseReport,
  SupportChatMessage,
  SupportMessageDto,
  SupportThread,
  TwentyIPackageTypesResponse,
  UpdateProvisioningBody,
  UpsertAddOnRequest,
  UpsertPlanRequest
} from "@/types/api";

export interface PurchaseReportFilters {
  from?: string;
  to?: string;
  customer?: string;
  planType?: string;
  status?: string;
  cycle?: string;
  page?: number;
  pageSize?: number;
}

/**
 * Single hook file for the entire admin surface — small enough that
 * splitting per-resource would be cargo-culted ceremony.
 */

// ── Overview ────────────────────────────────────────────────
export function useAdminOverview() {
  return useQuery({
    queryKey: ["admin", "overview"],
    queryFn: ({ signal }) => api.get<AdminOverviewDto>("/api/admin/overview", signal),
    staleTime: 30_000
  });
}

// ── Customers ───────────────────────────────────────────────
export function useAdminCustomers(args: { search?: string; page?: number; pageSize?: number }) {
  const { search = "", page = 1, pageSize = 50 } = args;
  return useQuery({
    queryKey: ["admin", "customers", { search, page, pageSize }],
    queryFn: ({ signal }) => {
      const params = new URLSearchParams();
      if (search) params.set("search", search);
      params.set("page", String(page));
      params.set("pageSize", String(pageSize));
      return api.get<AdminPagedResult<AdminCustomerSummaryDto>>(`/api/admin/customers?${params}`, signal);
    },
    staleTime: 15_000
  });
}

export function useAdminCustomerDetail(id: number | undefined) {
  return useQuery({
    queryKey: ["admin", "customers", id],
    enabled: id != null && !Number.isNaN(id),
    queryFn: ({ signal }) =>
      api.get<AdminCustomerDetailDto>(`/api/admin/customers/${id}`, signal)
  });
}

// ── Customer discounts (admin concessions via Stripe coupons) ──
export interface ApplyDiscountRequest {
  type: "percent" | "amount";
  value: number;
  duration: "once" | "repeating" | "forever";
  duration_in_months?: number;
  name?: string;
}

export function useApplyDiscount(id: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: ApplyDiscountRequest) =>
      api.post(`/api/admin/customers/${id}/discount`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin", "customers", id] })
  });
}

export function useRemoveDiscount(id: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.delete(`/api/admin/customers/${id}/discount`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin", "customers", id] })
  });
}

// Admin override for email verification (fallback if mail is down).
export function useSetEmailVerified(id: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (verified: boolean) =>
      api.post(`/api/admin/customers/${id}/verify-email`, { verified }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin", "customers", id] })
  });
}

// ── Provider operation logs (outbound calls to 20i/Openprovider/SES/Stripe) ──
export interface ProviderLogDto {
  id: number;
  created_at: string;
  provider: string;
  operation: string;
  status: string;
  target?: string | null;
  provider_reference?: string | null;
  code?: string | null;
  message?: string | null;
}

export function useProviderLogs(id: number | undefined) {
  return useQuery({
    queryKey: ["admin", "provider-logs", id],
    enabled: id != null && !Number.isNaN(id),
    queryFn: ({ signal }) =>
      api.get<AdminPagedResult<ProviderLogDto>>(
        `/api/admin/customers/${id}/provider-logs?pageSize=50`,
        signal
      )
  });
}

// ── 20i package types (on-demand, for mapping plans) ────────
// A mutation (not a query) so it only fires when the admin clicks
// "Load from 20i" in the plan editor — the 20i call is slowish.
export function useTwentyIPackageTypes() {
  return useMutation({
    mutationFn: () =>
      api.get<TwentyIPackageTypesResponse>("/api/admin/twentyi/package-types")
  });
}

// ── Leads ───────────────────────────────────────────────────
export function useAdminLeads(args: { status?: string; page?: number; pageSize?: number }) {
  const { status = "", page = 1, pageSize = 50 } = args;
  return useQuery({
    queryKey: ["admin", "leads", { status, page, pageSize }],
    queryFn: ({ signal }) => {
      const params = new URLSearchParams();
      if (status) params.set("status", status);
      params.set("page", String(page));
      params.set("pageSize", String(pageSize));
      return api.get<AdminPagedResult<AdminLeadDto>>(`/api/admin/leads?${params}`, signal);
    },
    staleTime: 15_000
  });
}

export function useUpdateLeadStatus(id: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (status: string) =>
      api.patch<AdminLeadDto>(`/api/admin/leads/${id}/status`, { status }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "leads"] });
      qc.invalidateQueries({ queryKey: ["admin", "overview"] });
    }
  });
}

// ── Webhooks ────────────────────────────────────────────────
export function useAdminWebhooks(args: {
  source?: string; eventType?: string; status?: string; page?: number; pageSize?: number;
}) {
  const { source = "", eventType = "", status = "", page = 1, pageSize = 100 } = args;
  return useQuery({
    queryKey: ["admin", "webhooks", { source, eventType, status, page, pageSize }],
    queryFn: ({ signal }) => {
      const params = new URLSearchParams();
      if (source)    params.set("source", source);
      if (eventType) params.set("eventType", eventType);
      if (status)    params.set("status", status);
      params.set("page", String(page));
      params.set("pageSize", String(pageSize));
      return api.get<AdminPagedResult<AdminWebhookEventDto>>(`/api/admin/webhooks?${params}`, signal);
    },
    staleTime: 10_000
  });
}

export function useAdminWebhook(id: number | undefined) {
  return useQuery({
    queryKey: ["admin", "webhooks", id],
    enabled: typeof id === "number",
    queryFn: ({ signal }) => api.get<AdminWebhookEventDto>(`/api/admin/webhooks/${id}`, signal)
  });
}

// ── Plans (admin CRUD via existing PlansController endpoints) ──
export function useAdminPlans() {
  return useQuery({
    queryKey: ["admin", "plans"],
    queryFn: ({ signal }) => api.get<PlanDto[]>("/api/plans?includeInactive=true", signal)
  });
}

export function useUpsertPlan() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: UpsertPlanRequest) =>
      api.post<PlanDto>("/api/plans", body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "plans"] });
      qc.invalidateQueries({ queryKey: ["plans"] });
      qc.invalidateQueries({ queryKey: ["admin", "overview"] });
    }
  });
}

export function useDeactivatePlan() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (slug: string) => api.delete(`/api/plans/${encodeURIComponent(slug)}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "plans"] });
      qc.invalidateQueries({ queryKey: ["plans"] });
      qc.invalidateQueries({ queryKey: ["admin", "overview"] });
    }
  });
}

// ── Provisioning / setup queue ─────────────────────────────
export function useAdminProvisioning(args: { status?: string; q?: string }) {
  const params = new URLSearchParams();
  if (args.status) params.set("status", args.status);
  if (args.q) params.set("q", args.q);
  return useQuery({
    queryKey: ["admin", "provisioning", args.status ?? "", args.q ?? ""],
    queryFn: ({ signal }) =>
      api.get<AdminProvisioningDto[]>(`/api/admin/provisioning?${params}`, signal)
  });
}

export function useUpdateProvisioning() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: number; body: UpdateProvisioningBody }) =>
      api.patch<{ success: boolean; status: string }>(`/api/admin/provisioning/${vars.id}`, vars.body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "provisioning"] });
      qc.invalidateQueries({ queryKey: ["admin", "overview"] });
    }
  });
}

// Real provisioning: create the 20i package now (addWeb) for a setup task.
export function useProvisionDomain() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, eppCode }: { id: number; eppCode?: string }) =>
      api.post<{ success: boolean; status?: string; message: string }>(
        `/api/admin/provisioning/${id}/provision-domain`,
        eppCode ? { epp_code: eppCode } : {}),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "provisioning"] });
    }
  });
}

export function useProvisionHosting() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) =>
      api.post<{ success: boolean; package_id?: string; message: string }>(
        `/api/admin/provisioning/${id}/provision-hosting`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "provisioning"] });
    }
  });
}

// ── Add-ons admin ──────────────────────────────────────────
export function useAdminAddOns() {
  return useQuery({
    queryKey: ["admin", "addons"],
    queryFn: ({ signal }) => api.get<AddOnDto[]>("/api/addons", signal)
  });
}

export function useUpsertAddOn() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: UpsertAddOnRequest) =>
      api.post<AddOnDto>("/api/addons", body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "addons"] });
      qc.invalidateQueries({ queryKey: ["addons"] });
      qc.invalidateQueries({ queryKey: ["admin", "overview"] });
    }
  });
}

export function useDeactivateAddOn() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (slug: string) => api.delete(`/api/addons/${encodeURIComponent(slug)}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "addons"] });
      qc.invalidateQueries({ queryKey: ["addons"] });
      qc.invalidateQueries({ queryKey: ["admin", "overview"] });
    }
  });
}

// ── Admin sync trigger (existing AdminSyncController) ───────
export function useRunUsageSync() {
  return useMutation({
    mutationFn: () => api.post<{
      containers_checked: number;
      containers_updated: number;
      containers_failed: number;
      elapsed_ms: number;
      failure_notes: string[];
    }>("/api/admin/sync/usage")
  });
}

// ── Renewal-reminder trigger ───────────────────────────────
export function useRunReminders() {
  return useMutation({
    mutationFn: () => api.post<{
      plans_checked: number;
      domains_checked: number;
      reminders_sent: number;
      elapsed_ms: number;
    }>("/api/admin/sync/reminders")
  });
}

// ── Admin custom email ─────────────────────────────────────
export function useSendAdminEmail() {
  return useMutation({
    mutationFn: (vars: { to_email: string; subject: string; message: string }) =>
      api.post<{ success: boolean; sent_to: string }>("/api/admin/email", vars)
  });
}

// ── Support inbox ──────────────────────────────────────────
export function useAdminSupport(status?: string) {
  return useQuery({
    queryKey: ["admin-support", status ?? "all"],
    queryFn: ({ signal }) =>
      api.get<SupportMessageDto[]>(
        `/api/admin/support${status ? `?status=${status}` : ""}`, signal)
  });
}

export function useResolveSupport() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.patch<{ success: boolean }>(`/api/admin/support/${id}/resolve`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin-support"] })
  });
}

export function useAdminSupportThread(id: number | undefined) {
  return useQuery({
    enabled: id != null,
    queryKey: ["admin-support-thread", id],
    queryFn: ({ signal }) => api.get<SupportThread>(`/api/admin/support/${id}`, signal)
  });
}

export function useReplySupport() {
  return useMutation({
    mutationFn: (vars: { id: number; body: string }) =>
      api.post<{ success: boolean; message: SupportChatMessage }>(
        `/api/admin/support/${vars.id}/replies`,
        { body: vars.body }
      )
  });
}

// ── Purchase / revenue report ──────────────────────────────
export function useAdminPurchaseReport(filters: PurchaseReportFilters) {
  return useQuery({
    queryKey: ["admin-report", filters],
    queryFn: ({ signal }) => {
      const p = new URLSearchParams();
      if (filters.from) p.set("from", filters.from);
      if (filters.to) p.set("to", filters.to);
      if (filters.customer) p.set("customer", filters.customer);
      if (filters.planType) p.set("planType", filters.planType);
      if (filters.status) p.set("status", filters.status);
      if (filters.cycle) p.set("cycle", filters.cycle);
      p.set("page", String(filters.page ?? 1));
      p.set("pageSize", String(filters.pageSize ?? 20));
      return api.get<PurchaseReport>(`/api/admin/reports/purchases?${p}`, signal);
    }
  });
}
